import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import TransactionLogger from '../../utils/transactionLogger';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import {
  Visibility,
  Refresh
} from '@mui/icons-material';

export default function TransactionLog() {
  const { userProfile } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [filter, setFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const transactionLogs = await TransactionLogger.getTransactionLogs(200);
      setLogs(transactionLogs);
    } catch (error) {
      console.error('Error fetching transaction logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE': return 'success';
      case 'UPDATE': return 'warning';
      case 'DELETE': return 'error';
      case 'LOGIN': return 'info';
      case 'LOGOUT': return 'info';
      case 'LOGIN_FAILED': return 'error';
      default: return 'default';
    }
  };

  const getEntityTypeColor = (entityType) => {
    switch (entityType) {
      case 'PLAYER': return 'primary';
      case 'TOURNAMENT': return 'secondary';
      case 'MATCH': return 'info';
      case 'SCORE': return 'success';
      case 'USER': return 'warning';
      default: return 'default';
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesFilter = filter === 'ALL' || log.entityType === filter;
    const matchesSearch = searchTerm === '' || 
      log.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.entityId.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setOpenDialog(true);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp.seconds * 1000).toLocaleString();
  };

  if (loading) {
    return <Container><Typography>Loading transaction logs...</Typography></Container>;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Transaction Log</Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchLogs}
        >
          Refresh
        </Button>
      </Box>

      <Box display="flex" gap={2} mb={3}>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Filter by Type</InputLabel>
          <Select
            value={filter}
            label="Filter by Type"
            onChange={(e) => setFilter(e.target.value)}
          >
            <MenuItem value="ALL">All Types</MenuItem>
            <MenuItem value="PLAYER">Players</MenuItem>
            <MenuItem value="TOURNAMENT">Tournaments</MenuItem>
            <MenuItem value="MATCH">Matches</MenuItem>
            <MenuItem value="SCORE">Scores</MenuItem>
            <MenuItem value="USER">Users</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by user or entity ID"
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Entity Type</TableCell>
              <TableCell>Entity ID</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>
                  {log.timestamp ? formatTimestamp(log.timestamp) : 'N/A'}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={log.action} 
                    color={getActionColor(log.action)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={log.entityType} 
                    color={getEntityTypeColor(log.entityType)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{log.entityId}</TableCell>
                <TableCell>{log.userEmail}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleViewDetails(log)}
                    title="View Details"
                  >
                    <Visibility />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Transaction Details Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Transaction Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Typography variant="h6" gutterBottom>Basic Information</Typography>
              <Box display="flex" flexDirection="column" gap={1} mb={3}>
                <Typography><strong>Action:</strong> {selectedLog.action}</Typography>
                <Typography><strong>Entity Type:</strong> {selectedLog.entityType}</Typography>
                <Typography><strong>Entity ID:</strong> {selectedLog.entityId}</Typography>
                <Typography><strong>User:</strong> {selectedLog.userEmail}</Typography>
                <Typography><strong>Timestamp:</strong> {selectedLog.timestamp ? formatTimestamp(selectedLog.timestamp) : 'N/A'}</Typography>
              </Box>

              {selectedLog.oldData && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>Previous Data</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                      {JSON.stringify(selectedLog.oldData, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}

              {selectedLog.newData && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>New Data</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                      {JSON.stringify(selectedLog.newData, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}





